import { addDays, addMonths, subDays, subMonths } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Icon from 'components/atomic/Icon';
import DateInput from 'components/atomic/controls/DateInput';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spaces.regular}px;

  @media ${theme.devices.tablet} {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.small2X}px;
  flex-grow: 1;

  .react-datepicker-wrapper {
    width: 100%;

    input {
      font-weight: 500;
      cursor: pointer;
    }
  }

  @media ${theme.devices.tablet} {
    width: 100%;
  }
`;

const StyledNavContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;
`;

const DateRangeFilter = ({
  shiftByMonth,
  nameFrom,
  nameTo,
  valueFrom = new Date(),
  valueTo = new Date(),
  onSelect,
}) => {
  const add = shiftByMonth ? addMonths : addDays;
  const sub = shiftByMonth ? subMonths : subDays;

  const handleMoveBack = () => {
    onSelect && onSelect({ [nameFrom]: sub(valueFrom, 1), [nameTo]: sub(valueTo, 1) });
  };

  const handleMoveForward = () => {
    onSelect && onSelect({ [nameFrom]: add(valueFrom, 1), [nameTo]: add(valueTo, 1) });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    onSelect({ [name]: value });
  };

  return (
    <StyledContainer>
      <StyledInputContainer>
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
          From:
        </Text>
        <DateInput
          small
          textColor={theme.colors.gray.secondary}
          backgroundColor={hexToRGBA(theme.colors.gray.secondary, 0.08)}
          icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
          name={nameFrom}
          value={valueFrom}
          maxDate={valueTo}
          dateFormat="MMMM dd, yyyy"
          onChange={handleChange}
        />
      </StyledInputContainer>
      <StyledInputContainer>
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
          To:
        </Text>
        <DateInput
          small
          textColor={theme.colors.gray.secondary}
          backgroundColor={hexToRGBA(theme.colors.gray.secondary, 0.08)}
          icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
          name={nameTo}
          value={valueTo}
          dateFormat="MMMM dd, yyyy"
          onChange={handleChange}
        />
      </StyledInputContainer>
      <StyledNavContainer>
        <StyledIconContainer onClick={handleMoveBack}>
          <Icon alias={ICON_ALIASES.arrowLeft} color={theme.colors.gray.secondary} />
        </StyledIconContainer>
        <StyledIconContainer onClick={handleMoveForward}>
          <Icon alias={ICON_ALIASES.arrowRight} color={theme.colors.gray.secondary} />
        </StyledIconContainer>
      </StyledNavContainer>
    </StyledContainer>
  );
};

DateRangeFilter.propTypes = {
  shiftByMonth: PropTypes.bool,
  nameFrom: PropTypes.string,
  nameTo: PropTypes.string,
  valueFrom: PropTypes.object,
  valueTo: PropTypes.object,
  onSelect: PropTypes.func,
};

export default DateRangeFilter;
